import type { KeyResultUnitValue } from '@/constants/KeyResultUnit';

import { KeyResultUnit } from '@/constants/KeyResultUnit';

export interface KeyResultDisplayProgressInput {
    unit: KeyResultUnitValue;
    startValue: number;
    targetValue: number;
    currentValue: number;
}

/**
 * Clamps any percentage value to the `[0, 100]` integer range used everywhere a progress bar or
 * badge renders a width or a percentage label. Centralizing this (instead of each call site
 * re-deriving its own `Math.min(Math.max(..., 0), 100)`) keeps every progress display safe
 * against out-of-range or fractional server values.
 */
export const clampPercent = (value: number): number =>
    Math.min(Math.max(Math.round(value), 0), 100);

/** An objective or key result reads as complete at 100 %, in progress below. */
export const isProgressComplete = (percentage: number): boolean => percentage >= 100;

/**
 * Client-only, DISPLAY-only percentage for a key result row that has not been saved yet (the
 * create/edit form has no server-computed `percentage` to show while the user is still typing).
 * Mirrors `ProgressCalculator.CalculateKeyResultPercentage` on the backend: `YesNo` is binary
 * (0 or 100), every other unit interpolates between start and target, rounded and clamped to
 * [0, 100], stopping at 99 until the target is reached. Never used to derive a value that gets sent to the API — the backend remains the
 * sole source of truth for the persisted percentage.
 */
export const computeKeyResultDisplayPercentage = ({
    unit,
    startValue,
    targetValue,
    currentValue,
}: KeyResultDisplayProgressInput): number => {
    if (unit === KeyResultUnit.YES_NO) {
        return currentValue > 0 ? 100 : 0;
    }

    const range = targetValue - startValue;
    if (range === 0) {
        return currentValue >= targetValue ? 100 : 0;
    }

    const ratio = ((currentValue - startValue) / range) * 100;
    return ratio >= 100 ? 100 : Math.min(clampPercent(ratio), 99);
};

export interface KeyResultRangeInput {
    startValue: number;
    targetValue: number;
    value: number;
}

/**
 * A key result's current value held between its start and its target, whichever way the range
 * runs: a measure to reduce has its target below its start. Nothing beyond the target counts, so
 * the progress never reads above 100 %.
 */
export const clampToKeyResultRange = ({ startValue, targetValue, value }: KeyResultRangeInput) =>
    Math.min(Math.max(value, Math.min(startValue, targetValue)), Math.max(startValue, targetValue));

export interface KeyResultValueFromPercentageInput {
    unit: KeyResultUnitValue;
    startValue: number;
    targetValue: number;
    percentage: number;
}

/** The decimals a number is written with: none for a whole one. */
const decimalPlaces = (value: number): number => String(value).split('.')[1]?.length ?? 0;

/** What `formatQuantity` ever shows: a bar landing finer than this would set a value nobody reads. */
const DISPLAYED_DECIMALS = 2;

/**
 * The exact inverse of `computeKeyResultDisplayPercentage`: given a percentage (from dragging or
 * clicking the progress bar), derives the `currentValue` that would have produced it. `YesNo` is
 * binary -- 50% is the tipping point, matching the prototype's own bar-drop rule -- and every
 * other unit interpolates back across [start, target]. When start and target are equal (the
 * zero-range edge case `computeKeyResultDisplayPercentage` also special-cases) a full bar resolves
 * to the target, anything short of it to the start.
 *
 * The result is rounded to the precision its own bounds are written in: a count from 0 to 12 still
 * lands on whole units, while a target of 10.50 $ keeps its cents and a measure from 0 to 0.5 can
 * be read anywhere along the bar instead of only at its two ends. Rounding everything to the unit
 * lost that precision silently, and the value control beside the bar accepts it.
 */
export const computeKeyResultValueFromPercentage = ({
    unit,
    startValue,
    targetValue,
    percentage,
}: KeyResultValueFromPercentageInput): number => {
    if (unit === KeyResultUnit.YES_NO) {
        return percentage >= 50 ? 1 : 0;
    }

    const range = targetValue - startValue;
    if (range === 0) {
        return percentage >= 100 ? targetValue : startValue;
    }

    const scale =
        10 **
        Math.min(
            Math.max(decimalPlaces(startValue), decimalPlaces(targetValue)),
            DISPLAYED_DECIMALS,
        );

    return Math.round((startValue + (percentage / 100) * range) * scale) / scale;
};
